/**
 * Signup — self-service public signup with email-domain auto-routing.
 *
 * Visitors with an email whose domain is registered on a tenant's
 * `email_domains` are auto-attached as a Viewer. Anyone else is told
 * to use the existing /request-access flow.
 *
 * Mirrors the visual treatment of /login — same hero + form chrome —
 * so the surface feels like one continuous auth surface.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, API, TOKEN_KEY } from "@/lib/api";

export default function Signup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup`, { email, password, name });
      // Drop the token directly — AuthProvider picks it up on the next
      // /auth/me probe. We hard-reload so the AuthProvider re-bootstraps
      // cleanly from localStorage without an in-place context dance.
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success(`Welcome to ${data?.user?.tenant_name || "ERIO"}!`, { duration: 4000 });
      window.location.replace("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      // 403 = domain not registered; surface the request-access path
      // explicitly so users don't dead-end.
      if (err?.response?.status === 403) {
        toast.warning(msg, { duration: 8000 });
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  const input = "w-full rounded-lg border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ek-ink outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20";

  return (
    <div className="min-h-screen bg-gradient-to-br from-ek-purple/[0.04] via-white to-ek-purple/[0.02]"
         data-testid="signup-page">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="block text-xs font-semibold uppercase tracking-[0.18em] text-ek-purple">
            EKSELENS · ERIO
          </Link>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ek-ink">Create your account</h1>
          <p className="mt-2 text-sm text-ek-ink/65">
            Sign up with your work email. We&apos;ll auto-attach you to your
            organisation&apos;s workspace.
          </p>

          <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="signup-form">
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink/70">Full name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required
                     className={input} data-testid="signup-name" autoComplete="name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink/70">Work email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     className={input} data-testid="signup-email" autoComplete="email" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink/70">Password</label>
              <input type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required
                     className={input} data-testid="signup-password" autoComplete="new-password" />
              <p className="mt-1 text-[11px] text-ek-ink/55">At least 8 characters.</p>
            </div>

            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
                   data-testid="signup-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading}
                    className="ek-btn w-full py-3 text-sm font-semibold"
                    data-testid="signup-submit">
              {loading ? "Creating your account…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-ek-ink/60">
            Domain not registered yet?{" "}
            <Link to="/request-access" className="font-semibold text-ek-purple hover:underline"
                  data-testid="signup-request-access-link">
              Request access
            </Link>
          </p>
          <p className="mt-2 text-center text-xs text-ek-ink/60">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-ek-purple hover:underline"
                  data-testid="signup-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
